import React, { useEffect, useMemo, useRef, useState } from "react"
import { SubmitHandler, useForm, useWatch } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { Form } from "reactstrap"
import {
    AcquiringContactFields,
    CompanyNameAndCVRInput,
    DateInput,
    EFileType,
    EKycFlowType,
    FieldsetFormGroup,
    FloatingLabelInput,
    FormActionSection,
    FormFieldsSection,
    FormGroupWithCheckbox,
    FormInputColumnWithInfoColumn,
    FormSectionLeftIndent,
    IAcquiringContactFirstAndLastNameSubForm,
    ICompanyNameAndCVRInputModel,
    IFormNavigationControl,
    IKycFileDto,
    IKycFlowDto,
    IKycOrgDetails,
    IMultiDropDownListItem,
    IUploadFileModel,
    InputLabel,
    MixedMerchantDetails,
    MultiDropDownList,
    ShowErrorMessages,
    StandardButtonWithSpinner,
    TCountryCode,
    TitleWithAbsoluteHint,
    addYears,
    canEditKycFlow,
    deleteKycFileThunk,
    fetchKycDetailsThunk,
    filterShouldValidate,
    getStringLocaleDateFormat,
    hasAnyErrorsForFieldPathsAndTexts,
    hasValue,
    inputOnChangeForceCapitalLetters,
    kycSelectors,
    organizationSelectors,
    sortAscending,
    updateKycFlowThunk,
    uploadKycFilesThunk,
    useSelector,
    userSelectors,
    valFuncRequired,
    valFuncRequiredAndPattern,
    validationPatterns,
} from "swiipe.portal.shared"
import { digitealOmitSectors, digitealSectors, digitealSubSectors } from "../../data/digitealSectors"
import { useMerchantPreOnboardingDetails } from "../../services/merchantOfferService"
import { StoreState } from "../../store/StoreState"
import { merchantOfferTryAddKycEventThunk } from "../../store/thunks/merchantPreOnboardingThunks"
import { useReduxDispatch } from "../../store/useReduxDispatch"
import SubmitButton from "../buttons/SubmitButton"
import FileInput, { megabyteInBytes } from "../form/input/FileInput"
import { UploadedFilesOverviewText } from "../overview/UploadedFilesOverview"
import "./DigitealKycForm.scss"
import { IDigitealKycPerson } from "./DigitealPersonFields"
import DigitealPersons from "./DigitealPersons"

interface IDigitealKycForm {
    merchantDetails: MixedMerchantDetails
    flowType: EKycFlowType
    webshopId: string
    onGoBack: () => void
    viewMode?: boolean
    navigationControl?: IFormNavigationControl
    formStateUpdatedCallback: (state: "finished") => void
    flow: IKycFlowDto
    orgDetails: IKycOrgDetails
}

interface IKycIdSubForm {
    idCard: FileList
    personWithIdCard: FileList
    expiration?: string
}

interface IKycBankAccountSubForm {
    bankAccountVerification: FileList | null
    iban: string
}

interface IKycAssociationSubForm {
    files: FileList
    isAvailableOnline: boolean
}

interface IKycForm {
    contact: IAcquiringContactFirstAndLastNameSubForm
    company: ICompanyNameAndCVRInputModel
    id: IKycIdSubForm
    pep: {
        isPep: boolean
        pepDetails: string
    }
    association: IKycAssociationSubForm
    ubos: IDigitealKycPerson[]
    sectors: IMultiDropDownListItem[]
    bankAccount: IKycBankAccountSubForm
}

const DigitealKycForm = ({
    merchantDetails,
    flowType,
    webshopId,
    flow,
    orgDetails,
    onGoBack,
    formStateUpdatedCallback,
    viewMode,
    navigationControl,
}: IDigitealKycForm) => {
    const { t } = useTranslation()
    const dispatch = useReduxDispatch()
    const swMerchantId = merchantDetails.swMerchant.swMerchantId
    const { control, formState, handleSubmit, clearErrors, trigger, setError, reset, setValue, watch } = useForm<IKycForm>({
        mode: "onChange",
        reValidateMode: "onChange",
    })

    const userData = useSelector(userSelectors.userData)
    const webshopDetails = useSelector((state: StoreState) => kycSelectors.webshopDetails(state, webshopId))
    const canEdit = canEditKycFlow(flow)

    const [showSubmitErrors, setShowSubmitErrors] = useState(false)

    const fileClearers = useRef<Record<string, () => void>>({})
    const fileClearing = useMemo<{ register: (name: string, clear: () => void) => void }>(
        () => ({
            register: (name: string, clear: () => void) => {
                fileClearers.current[name] = clear
            },
        }),
        []
    )

    const savedFields = useMemo(() => ({}), [])

    const [showAdditionalFields, setShowAdditionalFields] = useState(false)
    const preOnboardingDetails = useMerchantPreOnboardingDetails()
    const companyDetails = useSelector((state: StoreState) =>
        organizationSelectors.companyData(state, merchantDetails.swMerchant.registrationNumber)
    )

    const isPep = useWatch({
        name: "pep.isPep",
        control,
    })
    const isAvailableOnline = useWatch({
        name: "association.isAvailableOnline",
        control,
    })
    useEffect(() => {
        trigger()
    }, [isPep, isAvailableOnline])

    const [uboCount, setUboCount] = useState(1)

    const [saveJustTriggered, setSaveJustTriggered] = useState(false)

    const fileTypeError = t("fileupload.fileTypeError", { types: ".png, .jpg, .jpeg, .pdf" })

    const contactInfoValidationField: (keyof IAcquiringContactFirstAndLastNameSubForm)[] = [
        "firstName",
        "lastName",
        "email",
        "gender",
        "country",
        "dateOfBirth",
    ]

    const companyInfoValidationField: (keyof ICompanyNameAndCVRInputModel)[] = [
        "companyAddress",
        "companyCity",
        "companyName",
        "companyNumber",
        "companyPostcode",
        "country",
    ]

    const validatedPepFields = isPep ? ["pep.pepDetails"] : []
    const filesFields = ["id.idCard", "id.personWithIdCard", "association.files", "bankAccount.bankAccountVerification"]

    const idValidationField: (keyof IKycIdSubForm)[] = ["expiration", "idCard", "personWithIdCard"]
    const uboValidationField: string[] = Array.from({ length: uboCount }, (_, index) => [
        `ubos[${index}].firstName`,
        `ubos[${index}].lastName`,
        `ubos[${index}].dateOfBirth`,
        `ubos[${index}].placeOfBirth`,
        `ubos[${index}].placeOfBirth`,
    ]).flat()

    const sectorsValidationField: (keyof IKycForm)[] = ["sectors"]
    const bankAccountValidationField: (keyof IKycBankAccountSubForm)[] = ["bankAccountVerification", "iban"]
    const associationValidationField: (keyof IKycAssociationSubForm)[] = isAvailableOnline ? [] : ["files"]

    const resetForm = (org: IKycOrgDetails | undefined) => {
        reset({
            contact: {
                firstName: org?.contact?.firstName || userData?.user?.firstName,
                lastName: org?.contact?.lastName || userData?.user?.lastName,
                gender: org?.contact?.gender,
                dateOfBirth: org?.contact?.dateOfBirth,
                country: org?.contact?.countryCode,
                email: org?.contact?.email || userData?.user?.email,
            },
            company: {
                companyAddress: org?.orgAddress || companyDetails?.companyAddress,
                companyCity: org?.orgCity || companyDetails?.companyCity,
                companyName: org?.orgName || companyDetails?.companyName,
                companyPostcode: org?.orgPostCode || companyDetails?.companyPostcode,
                companyNumber: org?.orgVatNumber || merchantDetails.swMerchant.registrationNumber,
                country: (org?.orgCountryCode as TCountryCode) || (merchantDetails.swMerchant.countryCode as TCountryCode),
            },
            association: {
                isAvailableOnline: org?.companyAssociation?.isAvailableOnline,
            },
            bankAccount: {
                iban: org?.bankAccount?.iban,
                bankAccountVerification: null,
            },
            id: {
                expiration: org?.idCard?.expiration,
            },
            pep: {
                isPep: org?.pep?.isPep,
                pepDetails: org?.pep?.pepDetails,
            },
            ubos:
                org?.ubos?.map((p) => ({
                    dateOfBirth: p.dateOfBirth,
                    firstName: p.firstName,
                    id: p.personId,
                    isPep: p.isPep,
                    lastName: p.lastName,
                    placeOfBirth: p.placeOfBirth,
                })) ?? [],
            sectors: org?.sectors?.map((s) => ({ value: s.name, info: s.details })) ?? [],
        })
        setUboCount(org?.ubos?.length || 1)
        Object.values(fileClearers.current).forEach((clear) => clear())
        trigger()
        setTimeout(() => {
            trigger()
            // Activity sector needs a longer timeout to work for some reason
            setTimeout(() => {
                trigger()
            }, 100)
        })
    }

    useEffect(() => {
        resetForm(orgDetails)
    }, [])

    const handleSave = async (data: IKycForm, isSubmit: boolean) => {
        try {
            const createUploadFileModel = (files: FileList, fileType: EFileType): IUploadFileModel => {
                return {
                    files,
                    relationId: merchantDetails.swMerchant.swMerchantId,
                    type: fileType,
                    kycDetailsType: "Organization",
                    kycFlowId: flow.flowId,
                }
            }
            await Promise.all(
                [
                    dispatch(uploadKycFilesThunk(createUploadFileModel(data.association.files, "CompanyAssociation"))),
                    data.bankAccount.bankAccountVerification &&
                        dispatch(
                            uploadKycFilesThunk(
                                createUploadFileModel(data.bankAccount.bankAccountVerification, "BankAccountVerification")
                            )
                        ),
                    dispatch(uploadKycFilesThunk(createUploadFileModel(data.id.idCard, "IdCard"))),
                    dispatch(uploadKycFilesThunk(createUploadFileModel(data.id.personWithIdCard, "PersonWithIdCard"))),
                ].filter(hasValue)
            )

            const organization = await dispatch(
                updateKycFlowThunk(
                    flow,
                    isSubmit ? "SentComplete" : "DataSaved",
                    {
                        contact: {
                            personId: orgDetails?.contact?.personId,
                            firstName: data.contact.firstName,
                            lastName: data.contact.lastName,
                            gender: data.contact.gender,
                            countryCode: data.contact.country,
                            dateOfBirth: data.contact.dateOfBirth,
                            email: data.contact.email || orgDetails?.contact?.email,
                        },
                        bankAccount: {
                            iban: data.bankAccount.iban,
                        },
                        companyAssociation: {
                            isAvailableOnline: data.association.isAvailableOnline,
                        },
                        idCard: {
                            expiration: data.id.expiration,
                        },
                        orgAddress: data.company.companyAddress || companyDetails?.companyAddress,
                        orgCity: data.company.companyCity || companyDetails?.companyCity,
                        orgPostCode: data.company.companyPostcode || companyDetails?.companyPostcode,
                        orgCountryCode: data.company.country || (merchantDetails.swMerchant.countryCode as TCountryCode),
                        orgName: data.company.companyName || companyDetails?.companyName,
                        orgVatNumber: data.company.companyNumber || merchantDetails.swMerchant.registrationNumber,
                        pep: {
                            isPep: data.pep.isPep,
                            pepDetails: data.pep.pepDetails,
                        },
                        sectors: data.sectors.map((s) => ({ name: s.value, details: s.info })),
                        ubos: data.ubos.map((p) => ({
                            ...(orgDetails.ubos?.find((op) => p.id && op.personId === p.id) ?? {}),
                            dateOfBirth: p.dateOfBirth,
                            firstName: p.firstName,
                            lastName: p.lastName,
                            isPep: p.isPep,
                            placeOfBirth: p.placeOfBirth,
                        })),
                    },
                    undefined
                )
            )
            if (isSubmit) {
                dispatch(merchantOfferTryAddKycEventThunk(["Digiteal"], flow.flowId))
            }
            resetForm(organization)
        } catch (err) {
            // For keeping spinner
        }
    }

    const onSubmit: SubmitHandler<IKycForm> = async (data) => {
        await handleSave(data, true)
    }

    const isLastPage = true

    const renderSavedFiles = (files?: IKycFileDto[]) => (
        <UploadedFilesOverviewText
            files={files?.map((file) => ({
                name: file.uploadedFileName,
                id: file.fileId,
                status: file.isSubmitted ? "submitted" : "saved",
                canRemove: !file.isSubmitted,
            }))}
            onRemoveFile={async (file) => {
                await dispatch(deleteKycFileThunk(flow.organizationId, file.id!))
                await dispatch(fetchKycDetailsThunk(flow.organizationId, flow.organizationType, flow.webshopId, true))
            }}
        />
    )

    return (
        <div className="digiteal-kyc-form">
            <Form onSubmit={handleSubmit(onSubmit)}>
                {preOnboardingDetails?.preOnboarding?.merchantKycMessage &&
                    flow.state === "Initiated" &&
                    flow.subState === "None" && (
                        <FormFieldsSection
                            headerClassName="digiteal-kyc-form__sales-message-header"
                            header={t("digitealKyc.intro.commentTitleSales", { name: preOnboardingDetails.creator.firstName })}
                            validatedFields={[]}
                        >
                            <div className="digiteal-kyc-form__sales-message">
                                {preOnboardingDetails.preOnboarding.merchantKycMessage}
                            </div>
                        </FormFieldsSection>
                    )}

                {flow.requestedChangesForFields?.[""] &&
                    (flow.subState === "ChangesRequiredForComplete" || flow.subState === "ChangesRequiredForPartial") && (
                        <FormFieldsSection
                            headerClassName="digiteal-kyc-form__sales-message-header"
                            header={t("digitealKyc.intro.commentTitleDigiteal")}
                            validatedFields={[]}
                        >
                            <div className="digiteal-kyc-form__sales-message">{flow.requestedChangesForFields[""]}</div>
                        </FormFieldsSection>
                    )}

                <FormFieldsSection
                    header={`1. ${t("digitealKyc.contact.title")}`}
                    validatedFields={[...contactInfoValidationField.map((f) => "contact." + f)]}
                    formState={formState}
                    showErrors={showSubmitErrors}
                    showValidationCheckmark={true}
                >
                    <AcquiringContactFields<IKycForm>
                        control={control}
                        nameType="firstAndLastName"
                        showReview={false}
                        getFieldsFromControl={(form) => form?.contact}
                        savedFields={savedFields as IAcquiringContactFirstAndLastNameSubForm}
                        fieldSelectorPrefix="contact."
                        validatedFields={contactInfoValidationField.map((f) => "contact." + f)}
                        showGender
                        showDateOfBirth
                        showCountry
                        showEmail
                        useInputLabels
                        isEmailDisabled={flow.state !== "Initiated" || flow.subState !== "None"}
                    />
                </FormFieldsSection>
                <FormFieldsSection
                    header={`2. ${t("digitealKyc.contact.title")}`}
                    validatedFields={[...companyInfoValidationField.map((f) => "company." + f)]}
                    className="company-fields"
                    formState={formState}
                    showErrors={showSubmitErrors}
                    showValidationCheckmark={true}
                >
                    <FormInputColumnWithInfoColumn>
                        <CompanyNameAndCVRInput<IKycForm>
                            clearError={clearErrors}
                            errors={formState.errors.company}
                            getValues={() => control.getValues()}
                            register={control.register}
                            setError={setError}
                            setValue={setValue}
                            watch={watch}
                            fieldSelectorPrefix="company."
                            getModelFromForm={(form) => form?.company ?? {}}
                            isDisabled={true}
                            isInitiallyCompanyFieldsShown={true}
                            showCountry
                            useInputLabels
                        />
                    </FormInputColumnWithInfoColumn>
                </FormFieldsSection>
                <FormFieldsSection
                    header={`3. ${t("digitealKyc.id.title")}`}
                    validatedFields={[...idValidationField.map((f) => "id." + f)]}
                    formState={formState}
                    showValidationCheckmark={true}
                    showErrors={showSubmitErrors}
                >
                    <FormInputColumnWithInfoColumn>
                        <FieldsetFormGroup className="digiteal-kyc-form__id-expiration" field>
                            <InputLabel
                                title={`${t("digitealKyc.id.expiration.label")} (${getStringLocaleDateFormat().toUpperCase()})`}
                            >
                                <DateInput
                                    required
                                    name="id.expiration"
                                    errorMessage={t("digitealKyc.id.expiration.errorMessage")}
                                    formControl={control}
                                    valueDateFormat={"yyyy-MM-dd"}
                                    addFormatToPlaceholder
                                    maxDate={addYears(new Date(), 100)}
                                    minDate={new Date()}
                                    helpTypeSpecialChars
                                />
                            </InputLabel>
                        </FieldsetFormGroup>
                        <div>
                            <b>{t("digitealKyc.id.idCard.intro")}</b>
                        </div>
                        <FileInput
                            name="id.idCard"
                            accept=".png, .jpg, .jpeg, .pdf, image/png, image/jpeg, application/pdf"
                            requiredTypes={["application/pdf", "image/png", "image/jpeg"]}
                            wrongTypeFilesErrorMessage={t("fileupload.fileTypeError", { types: ".png, .jpg, .jpeg, .pdf" })}
                            currentFilesAmount={orgDetails?.idCard?.idCardFiles?.length ?? 0}
                            minFilesAmount={1}
                            maxFilesAmount={5}
                            maxFileSizeBytes={megabyteInBytes * 10}
                            clearError={clearErrors}
                            triggerValidation={control.trigger}
                            setError={setError}
                            setValue={control.setValue}
                            getValues={control.getValues}
                            register={control.register}
                            requiredErrorMessage={t("fileupload.errormessages.documentation")}
                            registerClearing={fileClearing}
                            multiple
                            showUnsavedWarning
                        />
                        {renderSavedFiles(orgDetails?.idCard?.idCardFiles)}
                        <div className="mt-3">
                            <b>{t("digitealKyc.id.holdingIdCard.intro")}</b>
                        </div>
                        <FileInput
                            name="id.personWithIdCard"
                            accept=".png, .jpg, .jpeg, .pdf, image/png, image/jpeg, application/pdf"
                            requiredTypes={["application/pdf", "image/png", "image/jpeg"]}
                            wrongTypeFilesErrorMessage={t("fileupload.fileTypeError", { types: ".png, .jpg, .jpeg, .pdf" })}
                            currentFilesAmount={orgDetails?.idCard?.personWithIdCardFiles?.length ?? 0}
                            minFilesAmount={1}
                            maxFilesAmount={5}
                            maxFileSizeBytes={megabyteInBytes * 10}
                            clearError={clearErrors}
                            triggerValidation={control.trigger}
                            setError={setError}
                            setValue={control.setValue}
                            getValues={control.getValues}
                            register={control.register}
                            requiredErrorMessage={t("fileupload.errormessages.documentation")}
                            registerClearing={fileClearing}
                            multiple
                            showUnsavedWarning
                        />
                        {renderSavedFiles(orgDetails?.idCard?.personWithIdCardFiles)}
                        <ShowErrorMessages
                            isHidden={showSubmitErrors}
                            formState={formState}
                            onlyForFieldNames={["id.idCard", "id.personWithIdCard"]}
                            onlyForTexts={[fileTypeError]}
                        />
                    </FormInputColumnWithInfoColumn>
                </FormFieldsSection>
                <FormFieldsSection
                    header={`4. ${t("digitealKyc.pep.title")}`}
                    validatedFields={validatedPepFields}
                    formState={formState}
                    showErrors={showSubmitErrors}
                    showValidationCheckmark={true}
                >
                    <FormInputColumnWithInfoColumn>
                        <FormGroupWithCheckbox
                            className="personal-checkbox"
                            innerRef={control.register}
                            name="pep.isPep"
                            title={t("digitealKyc.pep.isPep.label")}
                            hint={t("digitealKyc.pep.isPep.hint")}
                        />
                        {!!isPep && (
                            <FieldsetFormGroup className="acquiring-contact-fields__field" field>
                                <InputLabel title={t("digitealKyc.pep.pepDetails.label")}>
                                    <FloatingLabelInput
                                        name="pep.pepDetails"
                                        type="textarea"
                                        innerRef={control.register(
                                            filterShouldValidate(
                                                valFuncRequired(t("digitealKyc.pep.pepDetails.errorMessage")),
                                                "pep.pepDetails",
                                                validatedPepFields
                                            )
                                        )}
                                        hidePlaceholder={true}
                                    ></FloatingLabelInput>
                                </InputLabel>
                            </FieldsetFormGroup>
                        )}
                    </FormInputColumnWithInfoColumn>
                </FormFieldsSection>
                <FormFieldsSection
                    header={`5. ${t("digitealKyc.association.title")}`}
                    validatedFields={[...associationValidationField.map((f) => "association." + f)]}
                    formState={formState}
                    showValidationCheckmark={true}
                    showErrors={showSubmitErrors}
                >
                    <FormInputColumnWithInfoColumn>
                        {!isAvailableOnline && (
                            <>
                                <div>
                                    <b>{t("digitealKyc.association.intro")}</b>
                                </div>
                                <FileInput
                                    name="association.files"
                                    accept=".png, .jpg, .jpeg, .pdf, image/png, image/jpeg, application/pdf"
                                    requiredTypes={["application/pdf", "image/png", "image/jpeg"]}
                                    wrongTypeFilesErrorMessage={t("fileupload.fileTypeError", {
                                        types: ".png, .jpg, .jpeg, .pdf",
                                    })}
                                    currentFilesAmount={orgDetails?.companyAssociation?.companyAssociationFiles?.length ?? 0}
                                    minFilesAmount={1}
                                    maxFilesAmount={5}
                                    maxFileSizeBytes={megabyteInBytes * 10}
                                    clearError={clearErrors}
                                    triggerValidation={control.trigger}
                                    setError={setError}
                                    setValue={control.setValue}
                                    getValues={control.getValues}
                                    register={control.register}
                                    requiredErrorMessage={t("fileupload.errormessages.documentation")}
                                    registerClearing={fileClearing}
                                    multiple
                                    showUnsavedWarning
                                />

                                {renderSavedFiles(orgDetails?.companyAssociation?.companyAssociationFiles)}
                            </>
                        )}
                        <FormGroupWithCheckbox
                            className="mt-3"
                            innerRef={control.register}
                            name="association.isAvailableOnline"
                            title={t("digitealKyc.association.isAvailableOnline")}
                        />
                        <ShowErrorMessages
                            isHidden={showSubmitErrors}
                            formState={formState}
                            onlyForFieldNames={["association.files"]}
                            onlyForTexts={[fileTypeError]}
                        />
                    </FormInputColumnWithInfoColumn>
                </FormFieldsSection>
                <FormFieldsSection
                    header={`6. ${t("digitealKyc.sectors.title")}`}
                    validatedFields={sectorsValidationField}
                    formState={formState}
                    showErrors={showSubmitErrors}
                    showValidationCheckmark={true}
                >
                    <FormInputColumnWithInfoColumn
                        mobileInfoFirst
                        infoColumn={
                            <TitleWithAbsoluteHint
                                title={t("acquiringOnboarding.common.whyDoWeNeedInfo")}
                                hintText={t("digitealKyc.sectors.whyWeNeed")}
                            />
                        }
                    >
                        <MultiDropDownList
                            control={control}
                            name="sectors"
                            missingInputValueErrorText={t("digitealKyc.sectors.customInutErrorMessage")}
                            noValuesErrorText={t("digitealKyc.sectors.errorMessage")}
                            defaultText={t("digitealKyc.sectors.placeholder")}
                            label={t("digitealKyc.sectors.label")}
                            showInputForValue="OTHER"
                            isRequired
                            options={sortAscending(
                                digitealSectors
                                    .filter((sec) => !digitealOmitSectors.includes(sec))
                                    .map((sec) => {
                                        const parentSector = digitealSubSectors.find((s) => s.subSectors.includes(sec))
                                        return {
                                            text:
                                                (parentSector
                                                    ? `${t(`digitealKyc.sectors.keyToLabel.${parentSector.sector}`)} - `
                                                    : "") + t(`digitealKyc.sectors.keyToLabel.${sec}`),
                                            value: sec,
                                        }
                                    }),
                                (s) => (s.value === "OTHER" ? "ZZZ" : s.text)
                            )}
                        />
                    </FormInputColumnWithInfoColumn>
                </FormFieldsSection>
                <FormFieldsSection
                    header={`7. ${t("digitealKyc.ubos.title")}`}
                    validatedFields={uboValidationField}
                    formState={formState}
                    showValidationCheckmark={true}
                    fieldsIndentLeft="smaller"
                    showErrors={false}
                >
                    <FormSectionLeftIndent fullWidth>
                        <FormInputColumnWithInfoColumn
                            infoColumn={
                                <TitleWithAbsoluteHint
                                    title={t("acquiringOnboarding.common.whyDoWeNeedInfo")}
                                    hintText={t("digitealKyc.ubos.whyWeNeed")}
                                />
                            }
                        >
                            <span>
                                <div>
                                    <b>{t("digitealKyc.ubos.intro.title")}</b>
                                </div>

                                <ul>
                                    <li>{t("digitealKyc.ubos.intro.1")}</li>
                                    <li>{t("digitealKyc.ubos.intro.2")}</li>
                                    <li>{t("digitealKyc.ubos.intro.3")}</li>
                                    <li>{t("digitealKyc.ubos.intro.4")}</li>
                                </ul>
                            </span>
                        </FormInputColumnWithInfoColumn>
                    </FormSectionLeftIndent>
                    <DigitealPersons<IKycForm>
                        control={control}
                        clearError={clearErrors}
                        setError={setError}
                        formState={formState}
                        showErrors={showSubmitErrors}
                        fieldSelectorPrefix="ubos"
                        personCountState={[uboCount, (personCount) => setUboCount(personCount)]}
                    />
                </FormFieldsSection>
                <FormFieldsSection
                    header={`8. ${t("digitealKyc.bankAccount.title")}`}
                    validatedFields={[...bankAccountValidationField.map((f) => "bankAccount." + f)]}
                    formState={formState}
                    showValidationCheckmark={true}
                    showErrors={showSubmitErrors}
                >
                    <FormInputColumnWithInfoColumn>
                        <FieldsetFormGroup className="mb-0" field>
                            <InputLabel title={t("digitealKyc.bankAccount.iban.label")}>
                                <FloatingLabelInput
                                    placeholder={t("digitealKyc.bankAccount.iban.placeholder")}
                                    hintInsideInput={t("clearhausonboarding.messagetitles.ibanHint")}
                                    name="bankAccount.iban"
                                    type="text"
                                    disableFloating
                                    innerRef={control.register(
                                        valFuncRequiredAndPattern(
                                            validationPatterns.ibanNumber,
                                            t("digitealKyc.bankAccount.iban.formatErrorMessage"),
                                            t("digitealKyc.bankAccount.iban.errorMessage")
                                        )
                                    )}
                                    onChange={(e) => inputOnChangeForceCapitalLetters(e, "bankAccount.iban", control.setValue)}
                                ></FloatingLabelInput>
                            </InputLabel>
                        </FieldsetFormGroup>
                    </FormInputColumnWithInfoColumn>
                    <FormInputColumnWithInfoColumn
                        className="mt-3"
                        infoColumn={
                            <TitleWithAbsoluteHint
                                title={t("acquiringOnboarding.common.whyDoWeNeedInfo")}
                                hintText={t("digitealKyc.bankAccount.whyWeNeed")}
                            />
                        }
                    >
                        <div className="mb-1">
                            <div>
                                <b>{t("digitealKyc.bankAccount.uploadTitle")}</b>
                            </div>
                            <span>{t("digitealKyc.bankAccount.uploadDescription")}</span>
                        </div>
                    </FormInputColumnWithInfoColumn>
                    <FormInputColumnWithInfoColumn>
                        <FileInput
                            name="bankAccount.bankAccountVerification"
                            accept=".png, .jpg, .jpeg, .pdf, image/png, image/jpeg, application/pdf"
                            requiredTypes={["application/pdf", "image/png", "image/jpeg"]}
                            wrongTypeFilesErrorMessage={t("fileupload.fileTypeError", { types: ".png, .jpg, .jpeg, .pdf" })}
                            currentFilesAmount={orgDetails?.bankAccount?.bankAccountVerificationFiles?.length ?? 0}
                            minFilesAmount={1}
                            maxFilesAmount={5}
                            maxFileSizeBytes={megabyteInBytes * 10}
                            clearError={clearErrors}
                            triggerValidation={control.trigger}
                            setError={setError}
                            setValue={control.setValue}
                            getValues={control.getValues}
                            register={control.register}
                            requiredErrorMessage={t("fileupload.errormessages.documentation")}
                            registerClearing={fileClearing}
                            multiple
                            showUnsavedWarning
                        />
                        {renderSavedFiles(orgDetails?.bankAccount?.bankAccountVerificationFiles)}
                        <ShowErrorMessages
                            isHidden={showSubmitErrors}
                            formState={formState}
                            onlyForFieldNames={["bankAccount.bankAccountVerification"]}
                            onlyForTexts={[fileTypeError]}
                        />
                    </FormInputColumnWithInfoColumn>
                </FormFieldsSection>
                <FormActionSection
                    left={
                        <StandardButtonWithSpinner
                            isWide
                            inverted
                            disabled={hasAnyErrorsForFieldPathsAndTexts(formState.errors, filesFields, [fileTypeError])}
                            onClick={async () => {
                                const vals = control.getValues()
                                await handleSave(vals, false)
                            }}
                        >
                            {t("common.save")}
                        </StandardButtonWithSpinner>
                    }
                    middle={
                        <div>
                            <SubmitButton
                                onClick={() => setShowSubmitErrors(!!Object.keys(formState.errors).length)}
                                formState={formState}
                                dark
                                isPurple
                                noBorder
                            >
                                {isLastPage ? t("common.submit") : t("common.continue")}
                            </SubmitButton>
                        </div>
                    }
                />
            </Form>
        </div>
    )
}

export default DigitealKycForm
